import { useGlobalProperty } from '@/hooks/useGlobalProperty';
import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { propertyAPI, facturacionAPI, API } from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import { Building, User, Shield, Plus, BookImage, Pencil, MapPin, Upload, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export default function SettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useGlobalProperty(properties);
  const selectedProperty = properties.find(p => p.id === selectedPropertyId) || properties[0] || null;
  const [loading, setLoading] = useState(true);
  const [newPropOpen, setNewPropOpen] = useState(false);
  const [editMode, setEditMode] = useState(false); // false = create, true = edit existing
  const emptyProp = {
    nombre: '', tipo: 'Hotel', num_habitaciones: '', rnt: '',
    ciudad: '', departamento: '', direccion: '', telefono: '', email: '',
    camara_comercio: '', amenidades: [], google_maps_url: '', sitios_interes: '', descripcion: ''
  };
  const [newProp, setNewProp] = useState(emptyProp);
  const [creating, setCreating] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoVersion, setLogoVersion] = useState(0);
  const [hasLogo, setHasLogo] = useState(false);
  const logoFileRef = useRef(null);

  useEffect(() => {
    if (!selectedProperty?.id) return;
    facturacionAPI.getConfig(selectedProperty.id)
      .then(res => setHasLogo(!!res.data?.logo_path))
      .catch(() => {});
  }, [selectedProperty?.id]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedProperty?.id) return;
    setLogoUploading(true);
    try {
      await facturacionAPI.uploadLogo(selectedProperty.id, file);
      setHasLogo(true);
      setLogoVersion(v => v + 1);
      toast.success('Logo cargado correctamente. Se usará en el membrete de facturación.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir el logo');
    } finally {
      setLogoUploading(false);
      e.target.value = '';
    }
  };

  const AMENIDADES_LIST = [
    { code: 'parqueadero', label: 'Parqueadero' },
    { code: 'restaurante', label: 'Restaurante propio' },
    { code: 'ascensor', label: 'Ascensor' },
    { code: 'escaleras', label: 'Escaleras' },
    { code: 'desayuno_incluido', label: 'Desayuno incluido' },
    { code: 'wifi', label: 'Wi-Fi gratuito' },
    { code: 'aire_acondicionado', label: 'Aire acondicionado' },
    { code: 'piscina', label: 'Piscina' },
    { code: 'gimnasio', label: 'Gimnasio' },
    { code: 'lavanderia', label: 'Lavandería' },
    { code: 'recepcion_24h', label: 'Recepción 24 horas' },
    { code: 'servicio_habitaciones', label: 'Servicio de habitaciones' },
    { code: 'pet_friendly', label: 'Pet-friendly (mascotas)' },
    { code: 'accesibilidad', label: 'Accesibilidad movilidad reducida' },
    { code: 'sala_eventos', label: 'Sala de eventos / reuniones' },
    { code: 'facturacion_electronica', label: 'Facturación electrónica' },
  ];

  const toggleAmenidad = (code) => {
    setNewProp(p => {
      const cur = p.amenidades || [];
      return { ...p, amenidades: cur.includes(code) ? cur.filter(a => a !== code) : [...cur, code] };
    });
  };

  const openEditProperty = (prop) => {
    setEditMode(true);
    setNewProp({
      id: prop.id,
      nombre: prop.nombre || '',
      tipo: prop.tipo || 'Hotel',
      num_habitaciones: prop.num_habitaciones || '',
      rnt: prop.rnt || '',
      ciudad: prop.ciudad || '',
      departamento: prop.departamento || '',
      direccion: prop.direccion || '',
      telefono: prop.telefono || '',
      email: prop.email || '',
      camara_comercio: prop.camara_comercio || '',
      amenidades: prop.amenidades || [],
      google_maps_url: prop.google_maps_url || '',
      sitios_interes: prop.sitios_interes || '',
      descripcion: prop.descripcion || '',
    });
    setNewPropOpen(true);
  };

  const openCreateProperty = () => {
    setEditMode(false);
    setNewProp(emptyProp);
    setNewPropOpen(true);
  };

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await propertyAPI.getAll();
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Error al cargar propiedades');
    } finally {
      setLoading(false);
    }
  };

  const submitProperty = async (e) => {
    e.preventDefault();
    setCreating(true);
    const payload = {
      ...newProp,
      num_habitaciones: parseInt(newProp.num_habitaciones)
    };
    try {
      if (editMode && newProp.id) {
        const updatePayload = Object.fromEntries(Object.entries(payload).filter(([k]) => k !== 'id'));
        await propertyAPI.update(newProp.id, updatePayload);
        toast.success('Propiedad actualizada');
      } else {
        await propertyAPI.create(payload);
        toast.success('Propiedad registrada exitosamente');
      }
      setNewPropOpen(false);
      setNewProp(emptyProp);
      setEditMode(false);
      fetchProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar propiedad');
    } finally {
      setCreating(false);
    }
  };
  const handleCreateProperty = submitProperty;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Cargando configuración...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold" style={{fontFamily: 'Manrope'}} data-testid="settings-title">
            Configuración
          </h1>
          <p className="text-muted-foreground mt-1">
            Administra tu cuenta y propiedades
          </p>
        </div>

        {/* User Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Información de Usuario
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nombre</Label>
                <Input value={user?.nombre || ''} disabled />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={user?.email || ''} disabled />
              </div>
              <div className="space-y-2">
                <Label>Empresa</Label>
                <Input value={user?.empresa || 'No especificado'} disabled />
              </div>
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input value={user?.telefono || 'No especificado'} disabled />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Property Info */}
        {selectedProperty && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Building className="h-5 w-5" />
                  Información de Propiedad
                </CardTitle>
                <div className="flex items-center gap-2">
                  {properties.length > 1 && (
                    <Select 
                      value={selectedProperty?.id || ''} 
                      onValueChange={(id) => setSelectedPropertyId(id)}
                    >
                      <SelectTrigger className="w-[200px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {properties.map((prop) => (
                          <SelectItem key={prop.id} value={prop.id}>
                            {prop.nombre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <Button size="sm" onClick={() => openEditProperty(selectedProperty)} variant="outline" data-testid="edit-property-btn">
                    <Pencil className="h-4 w-4 mr-1" />
                    Editar propiedad
                  </Button>
                  <Button size="sm" onClick={() => navigate('/catalog')} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="catalog-btn">
                    <BookImage className="h-4 w-4 mr-1" />
                    Catálogo
                  </Button>
                  <Button size="sm" onClick={openCreateProperty} data-testid="register-property-btn">
                    <Plus className="h-4 w-4 mr-1" />
                    Registrar otra propiedad
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre de la Propiedad</Label>
                  <Input value={selectedProperty.nombre} disabled />
                </div>
                <div className="space-y-2">
                  <Label>RNT</Label>
                  <Input value={selectedProperty.rnt} disabled className="font-mono" />
                </div>
                <div className="space-y-2">
                  <Label>Tipo</Label>
                  <Input value={selectedProperty.tipo} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Número de Habitaciones</Label>
                  <Input value={selectedProperty.num_habitaciones} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Ciudad</Label>
                  <Input value={selectedProperty.ciudad} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Departamento</Label>
                  <Input value={selectedProperty.departamento} disabled />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Dirección</Label>
                  <Input value={selectedProperty.direccion} disabled />
                </div>
              </div>

              {/* Logo de facturación */}
              <div className="mt-4 pt-4 border-t space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <ImageIcon className="h-4 w-4 text-amber-600" /> Logo para facturación
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 border rounded-lg bg-slate-50 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {hasLogo ? (
                      <img
                        src={`${API}/facturacion/${selectedProperty.id}/logo?v=${logoVersion}`}
                        alt="Logo de la propiedad"
                        className="max-w-full max-h-full object-contain"
                        data-testid="settings-logo-preview"
                      />
                    ) : (
                      <ImageIcon className="h-6 w-6 text-slate-300" />
                    )}
                  </div>
                  <div>
                    <input type="file" accept="image/jpeg,image/png,image/webp" ref={logoFileRef} className="hidden" onChange={handleLogoUpload} data-testid="settings-logo-input" />
                    <Button size="sm" variant="outline" onClick={() => logoFileRef.current?.click()} disabled={logoUploading} data-testid="settings-logo-upload-btn">
                      <Upload className="h-4 w-4 mr-1" />
                      {logoUploading ? 'Subiendo...' : 'Cargar imagen del logo'}
                    </Button>
                    <p className="text-xs text-slate-500 mt-1">JPG, PNG o WEBP. Máx 5MB. Se usa en el membrete de remisiones y facturas.</p>
                  </div>
                </div>
              </div>

              {/* Datos chatbot */}
              {(selectedProperty.amenidades?.length > 0 || selectedProperty.sitios_interes || selectedProperty.google_maps_url || selectedProperty.camara_comercio) && (
                <div className="mt-4 pt-4 border-t space-y-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <MapPin className="h-4 w-4 text-blue-600" /> Información para el chatbot
                  </div>
                  {selectedProperty.camara_comercio && (
                    <div className="text-sm">
                      <span className="text-slate-500">Cámara de comercio: </span>
                      <span className="font-mono">{selectedProperty.camara_comercio}</span>
                    </div>
                  )}
                  {selectedProperty.amenidades?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {selectedProperty.amenidades.map(code => {
                        const a = AMENIDADES_LIST.find(x => x.code === code);
                        return (
                          <span key={code} className="text-xs bg-emerald-50 text-emerald-700 px-2 py-1 rounded-full border border-emerald-200">
                            {a?.label || code}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {selectedProperty.sitios_interes && (
                    <div className="text-sm">
                      <div className="text-slate-500 text-xs mb-1">Sitios de interés cercanos:</div>
                      <div className="bg-slate-50 rounded p-2 text-slate-700 whitespace-pre-wrap">{selectedProperty.sitios_interes}</div>
                    </div>
                  )}
                  {selectedProperty.google_maps_url && (
                    <div className="text-sm">
                      <span className="text-slate-500">Ubicación Google Maps: </span>
                      <a href={selectedProperty.google_maps_url} target="_blank" rel="noreferrer" className="text-blue-600 underline break-all">{selectedProperty.google_maps_url}</a>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Compliance */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Cumplimiento Legal
            </CardTitle>
            <CardDescription>
              Estado de integraciones fiscales y legales
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 border border-border rounded-lg">
              <div>
                <h4 className="font-semibold">RNT Validado</h4>
                <p className="text-sm text-muted-foreground">
                  Registro Nacional de Turismo
                </p>
              </div>
              <div className="text-green-600 font-semibold">Activo</div>
            </div>

            <div className="flex items-center justify-between p-4 border border-border rounded-lg">
              <div>
                <h4 className="font-semibold">DIAN - Facturación Electrónica</h4>
                <p className="text-sm text-muted-foreground">
                  Integración con sistema de facturación
                </p>
              </div>
              <div className="text-yellow-600 font-semibold">Pendiente</div>
            </div>

            <div className="bg-muted rounded-lg p-4">
              <p className="text-sm text-muted-foreground">
                Para completar la integración con DIAN y habilitar facturación electrónica, 
                contacta con nuestro equipo de soporte.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="border-destructive/50">
          <CardHeader>
            <CardTitle className="text-destructive">Zona Peligrosa</CardTitle>
            <CardDescription>
              Acciones irreversibles
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="destructive" disabled data-testid="delete-account-btn">
              Eliminar Cuenta
            </Button>
            <p className="text-sm text-muted-foreground mt-2">
              Contacta con soporte para solicitar la eliminación de tu cuenta
            </p>
          </CardContent>
        </Card>

        {/* Register / Edit Property Dialog */}
        <Dialog open={newPropOpen} onOpenChange={setNewPropOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                {editMode ? 'Editar Propiedad' : 'Registrar Nueva Propiedad'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateProperty} className="space-y-5">
              {/* Datos básicos */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-slate-700 border-b pb-1">Datos básicos</h4>
                <div className="space-y-2">
                  <Label>Nombre de la Propiedad</Label>
                  <Input placeholder="Mi Hotel" value={newProp.nombre} onChange={(e) => setNewProp({...newProp, nombre: e.target.value})} required data-testid="new-prop-nombre" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Tipo</Label>
                    <Select value={newProp.tipo} onValueChange={(v) => setNewProp({...newProp, tipo: v})}>
                      <SelectTrigger data-testid="new-prop-tipo"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Hotel">Hotel</SelectItem>
                        <SelectItem value="Apartamento">Apartamento</SelectItem>
                        <SelectItem value="Hostal">Hostal</SelectItem>
                        <SelectItem value="Posada">Posada</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Habitaciones</Label>
                    <Input type="number" min="1" placeholder="10" value={newProp.num_habitaciones} onChange={(e) => setNewProp({...newProp, num_habitaciones: e.target.value})} required data-testid="new-prop-habitaciones" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>RNT</Label>
                    <Input placeholder="12345" value={newProp.rnt} onChange={(e) => setNewProp({...newProp, rnt: e.target.value})} data-testid="new-prop-rnt" />
                  </div>
                  <div className="space-y-2">
                    <Label>Cámara de Comercio / NIT</Label>
                    <Input placeholder="900123456-7" value={newProp.camara_comercio} onChange={(e) => setNewProp({...newProp, camara_comercio: e.target.value})} data-testid="new-prop-camara" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Ciudad</Label>
                    <Input placeholder="Medellín" value={newProp.ciudad} onChange={(e) => setNewProp({...newProp, ciudad: e.target.value})} required data-testid="new-prop-ciudad" />
                  </div>
                  <div className="space-y-2">
                    <Label>Departamento</Label>
                    <Input placeholder="Antioquia" value={newProp.departamento} onChange={(e) => setNewProp({...newProp, departamento: e.target.value})} required data-testid="new-prop-departamento" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Dirección</Label>
                  <Input placeholder="Carrera 99 #66-43E" value={newProp.direccion} onChange={(e) => setNewProp({...newProp, direccion: e.target.value})} required data-testid="new-prop-direccion" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Teléfono</Label>
                    <Input placeholder="3001234567" value={newProp.telefono} onChange={(e) => setNewProp({...newProp, telefono: e.target.value})} required data-testid="new-prop-telefono" />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input type="email" placeholder="hotel@example.com" value={newProp.email} onChange={(e) => setNewProp({...newProp, email: e.target.value})} required data-testid="new-prop-email" />
                  </div>
                </div>
              </div>

              {/* Info para chatbot */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-slate-700 border-b pb-1 flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-blue-600" />
                  Información para el chatbot
                </h4>
                <p className="text-xs text-slate-500 -mt-2">Esta información se envía automáticamente cuando un cliente escribe al WhatsApp.</p>

                <div className="space-y-2">
                  <Label>Descripción corta</Label>
                  <Textarea
                    placeholder="Hotel boutique en el corazón de la ciudad..."
                    value={newProp.descripcion}
                    onChange={(e) => setNewProp({...newProp, descripcion: e.target.value})}
                    rows={2}
                    data-testid="new-prop-descripcion"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Amenidades / Servicios</Label>
                  <div className="grid grid-cols-2 gap-2 p-3 border rounded-lg max-h-56 overflow-y-auto">
                    {AMENIDADES_LIST.map(a => (
                      <label key={a.code} className="flex items-center gap-2 cursor-pointer text-sm hover:bg-slate-50 p-1.5 rounded">
                        <Checkbox
                          checked={(newProp.amenidades || []).includes(a.code)}
                          onCheckedChange={() => toggleAmenidad(a.code)}
                          data-testid={`amenidad-${a.code}`}
                        />
                        <span>{a.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Link de ubicación en Google Maps</Label>
                  <Input
                    placeholder="https://maps.google.com/..."
                    value={newProp.google_maps_url}
                    onChange={(e) => setNewProp({...newProp, google_maps_url: e.target.value})}
                    data-testid="new-prop-maps"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Sitios de interés cercanos</Label>
                  <Textarea
                    placeholder="Ej: Carrera 70 a 3 cuadras, Estación Estadio a 5 min caminando, cerca de Comuna 13..."
                    value={newProp.sitios_interes}
                    onChange={(e) => setNewProp({...newProp, sitios_interes: e.target.value})}
                    rows={4}
                    data-testid="new-prop-sitios"
                  />
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-2 border-t">
                <Button type="button" variant="outline" onClick={() => setNewPropOpen(false)}>Cancelar</Button>
                <Button type="submit" disabled={creating} data-testid="new-prop-submit">
                  {creating ? 'Guardando...' : (editMode ? 'Guardar cambios' : 'Registrar Propiedad')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
